import React, { useCallback } from 'react'
import { CellProps, useTable } from 'react-table'
import { SeedClass } from 'modules/domain/seedClass/types'
import SeedClassSelectors from 'modules/domain/seedClass/selectors'
import SeedClassActions from 'modules/domain/seedClass/duck'
import { useSeedClassList } from 'modules/domain/seedClass/hooks'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useHistory } from 'react-router-dom'
import Routes from '../routes'
import * as TComponents from 'views/components/CommonTableComponents/CommonTableComponents'
import { Progress } from 'modules/types'
import useDownload from 'hooks/useDownload'
import { endpoints } from 'modules/endpoints'
import {
  AdvancedHeadCell,
  Table,
  TableHead,
  TableHeadRow,
  TableBody,
  TableBodyRow,
  TableBodyCell,
  TableNoData,
  useAction,
  usePersistentScroll,
  Button,
} from '@agro-club/frontend-shared'
import { AlignWrapper } from 'views/components/AlignWrapper/AlignWrapper'

const SeedClassSortableHeadCell = AdvancedHeadCell<keyof SeedClass>()

const NameCell: React.FC<CellProps<SeedClass>> = ({ cell, column }) => {
  return <div key={column.id}>{cell.value}</div>
}

const CropCell: React.FC<CellProps<SeedClass>> = ({ cell, column }) => {
  return <div key={column.id}>{cell.value || ''}</div>
}

const SeedClassList: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation(['seedClass', 'labels'])
  const [progress, data] = useSeedClassList()
  const filterUpdated = useAction(SeedClassActions.filterUpdated)
  const sortingUpdated = useAction(SeedClassActions.sortingUpdated)
  const listRequested = useAction(SeedClassActions.listRequested)
  const filterValue = useSelector(SeedClassSelectors.filter)
  const total = useSelector(SeedClassSelectors.total)
  const pages = useSelector(SeedClassSelectors.pages)
  const page = useSelector(SeedClassSelectors.page)
  const pageSize = useSelector(SeedClassSelectors.pageSize)
  const { sort_field, sort_reversed } = useSelector(SeedClassSelectors.sorting)

  const visibleColumns = React.useMemo(
    () => [
      {
        Header: t('list.tableHeaders.name'),
        accessor: 'name' as const,
        Cell: NameCell,
        sortable: true,
      },
      {
        Header: t('list.tableHeaders.crop'),
        accessor: 'crop_name' as const,
        Cell: CropCell,
        sortable: false,
      },
    ],
    [t],
  )

  const { columns, rows, prepareRow } = useTable<SeedClass>({
    columns: visibleColumns,
    data: data as SeedClass[],
  })

  const handleClearFilters = useCallback(() => {
    filterUpdated({})
  }, [filterUpdated])

  const setPage = useCallback(
    (num) => {
      listRequested({ page: num })
    },
    [listRequested],
  )

  const { scrollRef } = usePersistentScroll('seedClassList')
  const [progressCSV, downloadCSV] = useDownload(endpoints.seedClass('download/csv'), {
    ...filterValue,
    sort_field,
    sort_reversed,
  })

  const isFilterApplied = Object.values(filterValue).some(Boolean)
  return (
    <TComponents.Wrapper>
      <TComponents.Filters>
        <TComponents.FiltersWrapper></TComponents.FiltersWrapper>
        <AlignWrapper verticalAlign="bottom" horizontalAlign="right">
          <Button onClick={downloadCSV} intent="primary" filled progress={progressCSV}>
            {t('labels:downloadCsv')}
          </Button>
        </AlignWrapper>
      </TComponents.Filters>
      <Table total={total} pages={pages} pageSize={pageSize} currentPage={page} onSetPage={setPage} ref={scrollRef}>
        <TableHead>
          <TableHeadRow>
            {columns.map((column) => {
              return (
                <SeedClassSortableHeadCell
                  key={column.getHeaderProps().key}
                  id={column.id as keyof SeedClass}
                  sortable={column.sortable}
                  hidden={column.hidden}
                  sortField={sort_field}
                  sortDesc={sort_reversed}
                  onChange={sortingUpdated}
                >
                  {column.render('Header')}
                </SeedClassSortableHeadCell>
              )
            })}
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...props } = row.getRowProps()
            return (
              <TableBodyRow
                key={key}
                {...props}
                onClick={() => {
                  history.push(generatePath(Routes.Edit, { id: row.original.id.toString() }))
                }}
              >
                {row.cells.map((cell) => {
                  const { key, ...props } = cell.getCellProps()
                  return (
                    <TableBodyCell key={key} {...props}>
                      {cell.render('Cell')}
                    </TableBodyCell>
                  )
                })}
              </TableBodyRow>
            )
          })}
          <TableNoData
            progress={progress}
            isEmpty={!rows.length}
            colSpan={visibleColumns.length}
            loading={<TComponents.Spinner />}
          >
            <div>{isFilterApplied ? t('list.emptyFilterMsg') : t('list.emptyMsg')}</div>
            {isFilterApplied && progress !== Progress.WORK && (
              <TComponents.ClearButton intent={'cancel'} size={'small'} onClick={handleClearFilters}>
                {t('list.resetAllFilters')}
              </TComponents.ClearButton>
            )}
          </TableNoData>
        </TableBody>
      </Table>
    </TComponents.Wrapper>
  )
}

export default SeedClassList
